import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  toast,
} from "react-toastify";

import {
  addTutorialFaq,
  addTutorialMedia,
  addTutorialSection,
  addTutorialStep,

  deleteTutorialFaq,
  deleteTutorialMedia,
  deleteTutorialSection,
  deleteTutorialStep,

  getTutorialById,

  updateTutorialFaq,
  updateTutorialMedia,
  updateTutorialSection,
  updateTutorialStep,
} from "../../../../services/tutorialServices";

const CLOSED_DRAWER = {
  open: false,
  type: null,
  mode: "create",
  data: null,
  parentId: null,
  context: null,
};

function sortByOrder(items = []) {
  return [...items].sort(
    (a, b) =>
      Number(a.ordre || 0) -
      Number(b.ordre || 0)
  );
}

function getNextOrder(
  items = []
) {
  if (!items.length) {
    return 10;
  }

  const highestOrder = Math.max(
    ...items.map((item) =>
      Number(item.ordre || 0)
    )
  );

  return highestOrder + 10;
}

export default function useTutorialEditor(
  tutorialId
) {
  const [tutorial, setTutorial] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState(null);

  const [
    actionLoading,
    setActionLoading,
  ] = useState(null);

  const [
    expandedSections,
    setExpandedSections,
  ] = useState(() => new Set());

  const [drawer, setDrawer] =
    useState(CLOSED_DRAWER);

  const [
    confirmation,
    setConfirmation,
  ] = useState(null);

  /* ------------------------------------------------------------------------ */
  /*                              CHARGEMENT                                  */
  /* ------------------------------------------------------------------------ */

  const loadTutorial = useCallback(
    async ({
      silent = false,
    } = {}) => {
      if (!tutorialId) {
        setError(
          "L’identifiant du tutoriel est manquant."
        );

        setLoading(false);
        return null;
      }

      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError(null);

        const data =
          await getTutorialById(
            tutorialId
          );

        if (!data) {
          throw new Error(
            "Tutoriel introuvable."
          );
        }

        setTutorial(data);

        const sectionIds = (
          data.sections || []
        ).map(
          (section) => section.id
        );

        setExpandedSections(
          (current) => {
            /*
             * Premier chargement :
             * ouverture de toutes les
             * sections.
             */
            if (!current.size) {
              return new Set(
                sectionIds
              );
            }

            /*
             * Suppression des identifiants
             * qui n'existent plus.
             */
            return new Set(
              [...current].filter(
                (sectionId) =>
                  sectionIds.includes(
                    sectionId
                  )
              )
            );
          }
        );

        return data;
      } catch (loadError) {
        console.error(
          "Erreur lors du chargement du tutoriel :",
          loadError
        );

        setError(
          "Impossible de charger ce tutoriel."
        );

        return null;
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [tutorialId]
  );

  useEffect(() => {
    loadTutorial();
  }, [loadTutorial]);

  /* ------------------------------------------------------------------------ */
  /*                               DONNÉES                                    */
  /* ------------------------------------------------------------------------ */

  const sortedSections = useMemo(
    () =>
      sortByOrder(
        tutorial?.sections || []
      ),
    [tutorial]
  );

  const sortedFaqs = useMemo(
    () =>
      sortByOrder(
        tutorial?.faqs || []
      ),
    [tutorial]
  );

  const statistics = useMemo(() => {
    const sections =
      tutorial?.sections || [];

    const steps = sections.flatMap(
      (section) =>
        section.steps || []
    );

    const medias = steps.flatMap(
      (step) => step.medias || []
    );

    const faqs =
      tutorial?.faqs || [];

    return {
      sections:
        sections.length,

      activeSections:
        sections.filter(
          (section) =>
            section.is_active !==
            false
        ).length,

      steps:
        steps.length,

      activeSteps:
        steps.filter(
          (step) =>
            step.is_active !== false
        ).length,

      medias:
        medias.length,

      activeMedias:
        medias.filter(
          (media) =>
            media.is_active !== false
        ).length,

      faqs:
        faqs.length,

      activeFaqs:
        faqs.filter(
          (faq) =>
            faq.is_active !== false
        ).length,
    };
  }, [tutorial]);

  /* ------------------------------------------------------------------------ */
  /*                         OUVERTURE DES SECTIONS                           */
  /* ------------------------------------------------------------------------ */

  const toggleSectionExpanded =
    useCallback((sectionId) => {
      setExpandedSections(
        (current) => {
          const next =
            new Set(current);

          if (next.has(sectionId)) {
            next.delete(sectionId);
          } else {
            next.add(sectionId);
          }

          return next;
        }
      );
    }, []);

  /* ------------------------------------------------------------------------ */
  /*                                DRAWERS                                   */
  /* ------------------------------------------------------------------------ */

  const resetDrawer =
    useCallback(() => {
      setDrawer(CLOSED_DRAWER);
    }, []);

  const openCreateSection =
    useCallback(() => {
      setDrawer({
        open: true,
        type: "section",
        mode: "create",
        data: null,
        parentId: tutorialId,
        context: {
          defaultOrder:
            getNextOrder(
              tutorial?.sections ||
                []
            ),
        },
      });
    }, [
      tutorialId,
      tutorial,
    ]);

  const openEditSection =
    useCallback(
      (section) => {
        setDrawer({
          open: true,
          type: "section",
          mode: "edit",
          data: section,
          parentId: tutorialId,
          context: null,
        });
      },
      [tutorialId]
    );

  const openCreateStep =
    useCallback(
      (sectionId) => {
        const section =
          tutorial?.sections?.find(
            (item) =>
              item.id === sectionId
          );

        setDrawer({
          open: true,
          type: "step",
          mode: "create",
          data: null,
          parentId: sectionId,
          context: {
            sectionTitle:
              section?.titre || "",

            defaultOrder:
              getNextOrder(
                section?.steps || []
              ),
          },
        });
      },
      [tutorial]
    );

  const openEditStep =
    useCallback(
      (step, sectionId) => {
        const section =
          tutorial?.sections?.find(
            (item) =>
              item.id === sectionId
          );

        setDrawer({
          open: true,
          type: "step",
          mode: "edit",
          data: step,
          parentId: sectionId,
          context: {
            sectionTitle:
              section?.titre || "",
          },
        });
      },
      [tutorial]
    );

  const openCreateMedia =
    useCallback((step) => {
      setDrawer({
        open: true,
        type: "media",
        mode: "create",
        data: null,
        parentId: step.id,
        context: {
          stepTitle:
            step.titre || "",

          defaultOrder:
            getNextOrder(
              step.medias || []
            ),
        },
      });
    }, []);

  const openEditMedia =
    useCallback(
      (media, step) => {
        setDrawer({
          open: true,
          type: "media",
          mode: "edit",
          data: media,
          parentId: step?.id || null,
          context: {
            stepTitle:
              step?.titre || "",
          },
        });
      },
      []
    );

  const openCreateFaq =
    useCallback(() => {
      setDrawer({
        open: true,
        type: "faq",
        mode: "create",
        data: null,
        parentId: tutorialId,
        context: {
          defaultOrder:
            getNextOrder(
              tutorial?.faqs || []
            ),
        },
      });
    }, [
      tutorialId,
      tutorial,
    ]);

  const openEditFaq =
    useCallback(
      (faq) => {
        setDrawer({
          open: true,
          type: "faq",
          mode: "edit",
          data: faq,
          parentId: tutorialId,
          context: null,
        });
      },
      [tutorialId]
    );

  const closeDrawer =
    useCallback(() => {
      if (actionLoading) {
        return;
      }

      resetDrawer();
    }, [
      actionLoading,
      resetDrawer,
    ]);

  /* ------------------------------------------------------------------------ */
  /*                       ENREGISTREMENT DRAWERS                             */
  /* ------------------------------------------------------------------------ */

  const saveDrawer = useCallback(
    async (payload) => {
      if (!drawer.type) {
        return false;
      }

      const actionKey =
        `save-${drawer.type}`;

      try {
        setActionLoading(
          actionKey
        );

        let response = null;

        switch (drawer.type) {
          case "section":
            response =
              drawer.mode ===
              "create"
                ? await addTutorialSection(
                    tutorialId,
                    payload
                  )
                : await updateTutorialSection(
                    drawer.data.id,
                    payload
                  );
            break;

          case "step":
            response =
              drawer.mode ===
              "create"
                ? await addTutorialStep(
                    drawer.parentId,
                    payload
                  )
                : await updateTutorialStep(
                    drawer.data.id,
                    payload
                  );
            break;

          case "media":
            response =
              drawer.mode ===
              "create"
                ? await addTutorialMedia(
                    drawer.parentId,
                    payload
                  )
                : await updateTutorialMedia(
                    drawer.data.id,
                    payload
                  );
            break;

          case "faq":
            response =
              drawer.mode ===
              "create"
                ? await addTutorialFaq(
                    tutorialId,
                    payload
                  )
                : await updateTutorialFaq(
                    drawer.data.id,
                    payload
                  );
            break;

          default:
            throw new Error(
              "Type de contenu non reconnu."
            );
        }

        if (!response) {
          throw new Error(
            "Aucune réponse retournée par le serveur."
          );
        }

        if (
          drawer.type ===
            "section" &&
          drawer.mode ===
            "create" &&
          response.id
        ) {
          setExpandedSections(
            (current) => {
              const next =
                new Set(current);

              next.add(response.id);

              return next;
            }
          );
        }

        const messages = {
          section:
            drawer.mode ===
            "create"
              ? "Section créée."
              : "Section mise à jour.",

          step:
            drawer.mode ===
            "create"
              ? "Étape créée."
              : "Étape mise à jour.",

          media:
            drawer.mode ===
            "create"
              ? "Média ajouté."
              : "Média mis à jour.",

          faq:
            drawer.mode ===
            "create"
              ? "Question créée."
              : "Question mise à jour.",
        };

        toast.success(
          messages[drawer.type] ||
            "Enregistrement effectué."
        );

        resetDrawer();

        await loadTutorial({
          silent: true,
        });

        return true;
      } catch (saveError) {
        console.error(
          "Erreur lors de l’enregistrement :",
          saveError
        );

        toast.error(
          "Impossible d’enregistrer les modifications."
        );

        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [
      drawer,
      loadTutorial,
      resetDrawer,
      tutorialId,
    ]
  );

  /* ------------------------------------------------------------------------ */
  /*                         ACTIVATION RAPIDE                                */
  /* ------------------------------------------------------------------------ */

  const performToggle =
    useCallback(
      async ({
        actionKey,
        updateAction,
        successMessage,
        errorMessage,
      }) => {
        try {
          setActionLoading(
            actionKey
          );

          const response =
            await updateAction();

          if (!response) {
            throw new Error(
              "La mise à jour n’a pas été confirmée."
            );
          }

          toast.success(
            successMessage
          );

          await loadTutorial({
            silent: true,
          });

          return true;
        } catch (toggleError) {
          console.error(
            "Erreur pendant la modification :",
            toggleError
          );

          toast.error(
            errorMessage
          );

          return false;
        } finally {
          setActionLoading(null);
        }
      },
      [loadTutorial]
    );

  const toggleSectionActive =
    useCallback(
      (section) =>
        performToggle({
          actionKey:
            `section-active-${section.id}`,

          updateAction: () =>
            updateTutorialSection(
              section.id,
              {
                is_active:
                  !section.is_active,
              }
            ),

          successMessage:
            section.is_active
              ? "Section désactivée."
              : "Section activée.",

          errorMessage:
            "Impossible de modifier la section.",
        }),
      [performToggle]
    );

  const toggleStepActive =
    useCallback(
      (step) =>
        performToggle({
          actionKey:
            `step-active-${step.id}`,

          updateAction: () =>
            updateTutorialStep(
              step.id,
              {
                is_active:
                  !step.is_active,
              }
            ),

          successMessage:
            step.is_active
              ? "Étape désactivée."
              : "Étape activée.",

          errorMessage:
            "Impossible de modifier l’étape.",
        }),
      [performToggle]
    );

  const toggleMediaActive =
    useCallback(
      (media) =>
        performToggle({
          actionKey:
            `media-active-${media.id}`,

          updateAction: () =>
            updateTutorialMedia(
              media.id,
              {
                is_active:
                  !media.is_active,
              }
            ),

          successMessage:
            media.is_active
              ? "Média désactivé."
              : "Média activé.",

          errorMessage:
            "Impossible de modifier le média.",
        }),
      [performToggle]
    );

  const toggleFaqActive =
    useCallback(
      (faq) =>
        performToggle({
          actionKey:
            `faq-active-${faq.id}`,

          updateAction: () =>
            updateTutorialFaq(
              faq.id,
              {
                is_active:
                  !faq.is_active,
              }
            ),

          successMessage:
            faq.is_active
              ? "Question désactivée."
              : "Question activée.",

          errorMessage:
            "Impossible de modifier la question.",
        }),
      [performToggle]
    );

  /* ------------------------------------------------------------------------ */
  /*                              CLASSEMENT                                  */
  /* ------------------------------------------------------------------------ */

  const moveSection = useCallback(
    async (
      sectionId,
      direction
    ) => {
      const rows = sortByOrder(
        tutorial?.sections || []
      );

      const currentIndex =
        rows.findIndex(
          (section) =>
            section.id ===
            sectionId
        );

      const targetIndex =
        direction === "up"
          ? currentIndex - 1
          : currentIndex + 1;

      if (
        currentIndex < 0 ||
        targetIndex < 0 ||
        targetIndex >= rows.length
      ) {
        return;
      }

      const currentSection =
        rows[currentIndex];

      const targetSection =
        rows[targetIndex];

      const actionKey =
        `move-section-${sectionId}`;

      try {
        setActionLoading(
          actionKey
        );

        const currentResponse =
          await updateTutorialSection(
            currentSection.id,
            {
              ordre:
                (targetIndex + 1) *
                10,
            }
          );

        const targetResponse =
          await updateTutorialSection(
            targetSection.id,
            {
              ordre:
                (currentIndex + 1) *
                10,
            }
          );

        if (
          !currentResponse ||
          !targetResponse
        ) {
          throw new Error(
            "Le nouvel ordre n’a pas été enregistré."
          );
        }

        await loadTutorial({
          silent: true,
        });
      } catch (moveError) {
        console.error(
          "Erreur de classement :",
          moveError
        );

        toast.error(
          "Impossible de déplacer la section."
        );
      } finally {
        setActionLoading(null);
      }
    },
    [
      tutorial,
      loadTutorial,
    ]
  );

  const moveStep = useCallback(
    async (
      sectionId,
      stepId,
      direction
    ) => {
      const section =
        tutorial?.sections?.find(
          (item) =>
            item.id === sectionId
        );

      if (!section) {
        return;
      }

      const rows = sortByOrder(
        section.steps || []
      );

      const currentIndex =
        rows.findIndex(
          (step) =>
            step.id === stepId
        );

      const targetIndex =
        direction === "up"
          ? currentIndex - 1
          : currentIndex + 1;

      if (
        currentIndex < 0 ||
        targetIndex < 0 ||
        targetIndex >= rows.length
      ) {
        return;
      }

      const currentStep =
        rows[currentIndex];

      const targetStep =
        rows[targetIndex];

      const actionKey =
        `move-step-${stepId}`;

      try {
        setActionLoading(
          actionKey
        );

        const currentResponse =
          await updateTutorialStep(
            currentStep.id,
            {
              ordre:
                (targetIndex + 1) *
                10,
            }
          );

        const targetResponse =
          await updateTutorialStep(
            targetStep.id,
            {
              ordre:
                (currentIndex + 1) *
                10,
            }
          );

        if (
          !currentResponse ||
          !targetResponse
        ) {
          throw new Error(
            "Le nouvel ordre n’a pas été enregistré."
          );
        }

        await loadTutorial({
          silent: true,
        });
      } catch (moveError) {
        console.error(
          "Erreur de classement :",
          moveError
        );

        toast.error(
          "Impossible de déplacer l’étape."
        );
      } finally {
        setActionLoading(null);
      }
    },
    [
      tutorial,
      loadTutorial,
    ]
  );

  /* ------------------------------------------------------------------------ */
  /*                       DEMANDES DE SUPPRESSION                            */
  /* ------------------------------------------------------------------------ */

  const requestDeleteSection =
    useCallback((section) => {
      setConfirmation({
        type: "section",
        data: section,

        title:
          "Supprimer cette section ?",

        description:
          "Toutes les étapes et tous les médias appartenant à cette section seront également supprimés.",

        confirmLabel:
          "Supprimer la section",
      });
    }, []);

  const requestDeleteStep =
    useCallback((step) => {
      setConfirmation({
        type: "step",
        data: step,

        title:
          "Supprimer cette étape ?",

        description:
          "Tous les médias associés à cette étape seront également supprimés.",

        confirmLabel:
          "Supprimer l’étape",
      });
    }, []);

  const requestDeleteMedia =
    useCallback((media) => {
      setConfirmation({
        type: "media",
        data: media,

        title:
          "Supprimer ce média ?",

        description:
          "Le média sera retiré de cette étape. Le fichier distant ne sera pas supprimé.",

        confirmLabel:
          "Supprimer le média",
      });
    }, []);

  const requestDeleteFaq =
    useCallback((faq) => {
      setConfirmation({
        type: "faq",
        data: faq,

        title:
          "Supprimer cette question ?",

        description:
        "Cette question et sa réponse ne seront plus disponibles dans le tutoriel.",

        confirmLabel:
          "Supprimer la question",
      });
    }, []);

  const closeConfirmation =
    useCallback(() => {
      if (actionLoading) {
        return;
      }

      setConfirmation(null);
    }, [actionLoading]);

  /* ------------------------------------------------------------------------ */
  /*                         CONFIRMATION SUPPRESSION                         */
  /* ------------------------------------------------------------------------ */

  const confirmDelete =
    useCallback(async () => {
      if (
        !confirmation?.data?.id
      ) {
        return;
      }

      const actionKey =
        `delete-${confirmation.type}-${confirmation.data.id}`;

      try {
        setActionLoading(
          actionKey
        );

        let response = null;

        switch (
          confirmation.type
        ) {
          case "section":
            response =
              await deleteTutorialSection(
                confirmation.data.id
              );
            break;

          case "step":
            response =
              await deleteTutorialStep(
                confirmation.data.id
              );
            break;

          case "media":
            response =
              await deleteTutorialMedia(
                confirmation.data.id
              );
            break;

          case "faq":
            response =
              await deleteTutorialFaq(
                confirmation.data.id
              );
            break;

          default:
            throw new Error(
              "Type de contenu non reconnu."
            );
        }

        if (!response) {
          throw new Error(
            "La suppression n’a pas été confirmée."
          );
        }

        const messages = {
          section:
            "Section supprimée.",

          step:
            "Étape supprimée.",

          media:
            "Média supprimé.",

          faq:
            "Question supprimée.",
        };

        toast.success(
          messages[
            confirmation.type
          ] ||
            "Élément supprimé."
        );

        setConfirmation(null);

        await loadTutorial({
          silent: true,
        });

        return true;
      } catch (deleteError) {
        console.error(
          "Erreur pendant la suppression :",
          deleteError
        );

        toast.error(
          "Impossible de supprimer cet élément."
        );

        return false;
      } finally {
        setActionLoading(null);
      }
    }, [
      confirmation,
      loadTutorial,
    ]);

  return {
    tutorial,
    sortedSections,
    sortedFaqs,
    statistics,

    loading,
    refreshing,
    error,
    actionLoading,

    expandedSections,
    toggleSectionExpanded,

    drawer,

    openCreateSection,
    openEditSection,

    openCreateStep,
    openEditStep,

    openCreateMedia,
    openEditMedia,

    openCreateFaq,
    openEditFaq,

    closeDrawer,
    saveDrawer,

    toggleSectionActive,
    toggleStepActive,
    toggleMediaActive,
    toggleFaqActive,

    moveSection,
    moveStep,

    confirmation,

    requestDeleteSection,
    requestDeleteStep,
    requestDeleteMedia,
    requestDeleteFaq,

    closeConfirmation,
    confirmDelete,

    loadTutorial,
  };
}